import { useMemo } from 'react'

import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { getRelativeDayInfo } from 'utils'

import { translations } from './video-status-description.i18n'
import type { IVideoStatusDescription, IVideoStatusDescriptionReturnProps } from './video-status-description.types'
import { ENTITY_UPDATE_STATUS, HOURS_SINCE_LAST_UPDATE_THRESHOLD, TWO_WEEKS_IN_MS } from '../common/posts.consts'
import { postsTranslations } from '../common/posts.i18n'

export const useVideoStatusDescriptionModel = ({
  statusPost,
  statusPostResponse,
  publishedAt,
  postUrl,
}: IVideoStatusDescription): IVideoStatusDescriptionReturnProps => {
  const t = useTranslate()
  const currentLanguage = useCurrentLanguage()

  const isVideoOlderThanTwoWeeks = useMemo(() => {
    if (!publishedAt) return true
    const publishedDate = new Date(publishedAt)
    const now = new Date()
    return now.getTime() - publishedDate.getTime() > TWO_WEEKS_IN_MS
  }, [publishedAt])

  const tooltipStatusMessage = useMemo(() => {
    if (
      statusPostResponse?.lastUpdateAt &&
      statusPost === ENTITY_UPDATE_STATUS.SUCCESS &&
      statusPostResponse?.hoursSinceLastUpdate != null &&
      statusPostResponse.hoursSinceLastUpdate <= HOURS_SINCE_LAST_UPDATE_THRESHOLD
    ) {
      const dayInfo = getRelativeDayInfo(statusPostResponse.lastUpdateAt, currentLanguage)
      if (!dayInfo) return ''

      return t(postsTranslations.tooltipUpdated({ day: dayInfo.isToday ? 'today' : 'yesterday', time: dayInfo.time }))
    }
    return ''
  }, [statusPost, statusPostResponse, t, currentLanguage])

  const textStatusPost = useMemo(() => {
    if (
      statusPost === ENTITY_UPDATE_STATUS.SUCCESS &&
      statusPostResponse?.hoursSinceLastUpdate != null &&
      statusPostResponse.hoursSinceLastUpdate <= HOURS_SINCE_LAST_UPDATE_THRESHOLD
    ) {
      return t(translations.postStateSuccess)
    }
    if (statusPost === ENTITY_UPDATE_STATUS.FAILED) {
      return t(translations.postStateFailed)
    }
    if (statusPost === ENTITY_UPDATE_STATUS.RUNNING) {
      return t(translations.postStateRunning)
    }
    if (!statusPost && isVideoOlderThanTwoWeeks) {
      return t(translations.postUpdatedMoreThanTwoWeeksAgo)
    }
    return ''
  }, [statusPost, statusPostResponse, t, isVideoOlderThanTwoWeeks])

  return { statusPost, tooltipStatusMessage, textStatusPost, postUrl }
}
